import { ChangeEvent, useState } from "react"
import { useIntl } from "react-intl"

import { DateValue, getLocalTimeZone, today } from "@internationalized/date"
import classnames from "classnames"
import dayjs from "dayjs"
import isoWeek from "dayjs/plugin/isoWeek"

import {
  Button,
  Checkbox,
  DatePicker,
  Dialog,
  GlobalSnackbarRegion,
  StaffBodyTextS,
  StaffHeading3,
  TextArea
} from "@doktor-se/bones-ui"
import CalendarAdd from "@doktor-se/bones-ui/dist/components/icons/components/CalendarAdd"
import { AssignedConversation } from "@doktor-se/bones-ui/dist/web-shared/types"

import { createRevisitBooking, RevisitBookingParams } from "api"
import { logAmplitudeEvent } from "lib/amplitude/amplitude"
import { categorySelectors } from "reducers/categories"

import { Offset } from "pages/dashboard/components/ConversationControls/ConversationControls"

import { useAppDispatch, useAppSelector } from "../../../lib/hooks"

import styles from "./RevisitsDialog.module.scss"

dayjs.extend(isoWeek)

export interface RevisitsDialogProps {
  isOpen: boolean
  conversation: AssignedConversation
  onClose: () => void
  offset?: Offset
}

const RevisitsDialog = ({ isOpen, conversation, offset, onClose }: RevisitsDialogProps) => {
  const intl = useIntl()
  const dateDisplayFormat = intl.locale === "en-GB" ? "dddd, MMMM Do" : "dddd, D MMMM"
  const dispatch = useAppDispatch()
  const queueOpeningHours = useAppSelector(state => state.app.queueOpeningHours)

  const [smsAgreement, setSmsAgreement] = useState(false)
  const [date, setDate] = useState<Date | null>(null)
  const [time, setTime] = useState<string>("00:00")
  const [error, setError] = useState({
    date: false,
    time: false
  })
  const [purposeText, setPurposeText] = useState("")
  const [isTimeChanged, setIsTimeChanged] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isValidTime = isTimeChanged && !!time
  const isPastDate = (date: DateValue) => date.compare(today(getLocalTimeZone())) < 0

  const onCloseDialog = () => {
    onClose()
  }

  const getOpeningHoursForDate = (date: Date | null) => {
    if (!date) return null

    const weekday = dayjs(date).isoWeekday() - 1 // Backend: monday = 0, Frontend: monday = 1

    const openingHour = queueOpeningHours.find(oh => oh.weekday === weekday && oh.queue === "doctor")

    return openingHour
  }

  const openingHour = getOpeningHoursForDate(date)
  const timeConstraints = openingHour ? { min: openingHour.openingTime, max: openingHour.closingTime } : undefined

  const getPreviousDayOpeningHours = (date: Date | null) => {
    if (!date) return null

    const previousDay = dayjs(date).subtract(1, "day").toDate()
    return getOpeningHoursForDate(previousDay)
  }

  const previousDayOpeningHour = getPreviousDayOpeningHours(date)

  const isValidHour = (time: string) => {
    if (!timeConstraints) {
      return true
    }

    let isValid = false

    const { min, max } = timeConstraints

    if (max > min) {
      isValid = time >= min && time <= max
    } else {
      isValid = time >= min
    }

    // NOTE: If previous day opening time 07:00-01:00, 00:00-01:00 shoiuld be available for the next day.
    if (!isValid && previousDayOpeningHour) {
      const prevMax = previousDayOpeningHour.closingTime
      const prevMin = previousDayOpeningHour.openingTime

      if (prevMax < prevMin) {
        isValid = time <= prevMax
      }
    }

    return isValid
  }

  const formatDateTime = (date: Date, time: string) => {
    const [hour, minute] = time.split(":").map(Number)
    const start = dayjs(date).hour(hour).minute(minute).second(0).millisecond(0)
    return start
  }

  const category = useAppSelector(state => categorySelectors.selectById(state, conversation.categoryId || 0))
  const success = () => {
    logAmplitudeEvent("carealot.book_followup.schedule_revisit_cta_pressed", {
      category_name: category?.name,
      category_id: conversation.categoryId,
      selected_timeframe: formatDateTime(date!, time).toISOString()
    })

    onClose()

    GlobalSnackbarRegion.queue.add(
      { text: intl.formatMessage({ id: "revisits.confirmation.toast" }), variant: "success" },
      { timeout: 4000 }
    )
  }
  const schedule = () => {
    if (isSubmitting) {
      return
    }
    setIsSubmitting(true)

    if (!date || !isValidTime || !isValidHour(time)) {
      setIsSubmitting(false)
      setError({
        time: !isValidTime,
        date: !date
      })
      return
    }

    const start = formatDateTime(date, time)

    const revisitParams: RevisitBookingParams = {
      client_id: conversation.patientId,
      start: start.toISOString(),
      // Automatically set end based on booking type
      note: purposeText,
      metadata: { sms_agreement: smsAgreement },
      conversation_id: conversation.id
    }

    dispatch(createRevisitBooking(revisitParams))
      .then(result => (result ? success() : undefined))
      .finally(() => setIsSubmitting(false))
  }

  const onDateChange = (date: Date) => {
    setDate(date)
    setError({
      ...error,
      date: false
    })
  }

  const onTextChange = (text: string) => {
    setPurposeText(text)
  }

  const onTimeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setIsTimeChanged(true)
    if (!value) {
      return
    }
    setTime(value)
    setError({
      ...error,
      time: false
    })
  }

  return (
    <Dialog
      isOpen={isOpen}
      offset={offset && { top: `${offset.top}px`, left: `${offset.left}px` }}
      onClose={onCloseDialog}
      closeAriaLabel={intl.formatMessage({ id: "modal.close" })}
      contentFullWidth>
      {
        <div className={styles.container}>
          <div className={styles.header}>
            <CalendarAdd width={31} height={33} />
            <StaffHeading3 className={styles.heading}>{intl.formatMessage({ id: "revisits.title" })}</StaffHeading3>

            {isValidTime && date ? (
              <StaffBodyTextS className={styles.datestring}>
                {dayjs(date).format(dateDisplayFormat) + " " + time}
              </StaffBodyTextS>
            ) : (
              <StaffBodyTextS>{intl.formatMessage({ id: "revisits.date.display.label" })}</StaffBodyTextS>
            )}
          </div>

          <div className={styles.form}>
            <DatePicker
              aria-label="Booking Date"
              onChange={onDateChange}
              value={date}
              fullWidth
              todayLabel={intl.formatMessage({ id: "calendar.datepicker.today" })}
              variant="onSurface"
              isDateUnavailable={isPastDate}
              errorMessage={error.date ? " " : undefined}
            />

            <input
              type="time"
              className={classnames(styles.input, styles.time, {
                [styles.inputError]: error.time || !isValidHour(time)
              })}
              value={time}
              onChange={onTimeChange}
            />

            {error.time || error.date ? (
              <span className={styles.validationError}>{intl.formatMessage({ id: "revisits.validation.error" })}</span>
            ) : (
              !isValidHour(time) && (
                <span className={styles.validationError}>
                  {intl.formatMessage({ id: "revisits.validation.time.hour.error" }) +
                    ` (${timeConstraints?.min} - ${timeConstraints?.max})`}
                </span>
              )
            )}

            <TextArea
              variant="onSurface"
              label={intl.formatMessage({ id: "revisits.textarea.label" })}
              value={purposeText}
              noBorder={true}
              onChange={onTextChange}
              fullWidth={true}
            />
            <div className={styles.checkbox}>
              <Checkbox
                label={intl.formatMessage({ id: "revisits.agreement" })}
                aria-label="Revisit SMS Checkbox"
                onChange={() => setSmsAgreement(!smsAgreement)}
              />
            </div>
            <div className={styles.buttonContainer}>
              <Button color="primary" variant="primary" fullWidth type="submit" onPress={schedule}>
                {intl.formatMessage({ id: "revisits.button" })}
              </Button>
            </div>
          </div>
        </div>
      }
    </Dialog>
  )
}
export default RevisitsDialog
